import { getScrollableAncestor } from "@prismicio/editor-support/DOM"
import { clamp } from "@prismicio/editor-support/Number"
import { useStableCallback } from "@prismicio/editor-support/React"
import { type RefObject, useCallback, useEffect, useRef } from "react"

// The closer to that threshold, the slower the scroll will be so users can adjust how fast to go.
const autoScrollAreaSize = 30
const autoScrollSpeed = 0.4

export interface AutoScrollOptions {
  /**
   * If an element is blocking part of the top area of the scroller,
   * set scrollTopOffset to its height so the scroll behavior takes it into account.
   */
  scrollOffsetTop?: number
}

export function useAutoScroll(
  ref: RefObject<Element>,
  options: AutoScrollOptions = {},
) {
  const { scrollOffsetTop = 0 } = options

  const scrollableRef = useRef<Element>()

  const state = useRef<AutoScrollState>({ dx: 0, dy: 0 }).current

  const stopTimer = useStableCallback(() => {
    if (!state.timer) return

    cancelAnimationFrame(state.timer)
    state.timer = undefined
  })

  useEffect(() => {
    if (!ref.current) return
    scrollableRef.current = getScrollableAncestor(ref.current)
  }, [ref])

  useEffect(() => stopTimer, [stopTimer])

  const scroll = useCallback(() => {
    if (!scrollableRef.current) return

    scrollableRef.current.scrollLeft += clampScrollIncrement(state.dx)
    scrollableRef.current.scrollTop += clampScrollIncrement(state.dy)

    if (state.timer) state.timer = requestAnimationFrame(scroll)
  }, [scrollableRef, state])

  const move = useStableCallback((evt: MouseEvent) => {
    if (!scrollableRef.current) return

    const x = evt.clientX
    const y = evt.clientY

    const box = getScrollerBox(scrollableRef.current)
    const left = box.left + autoScrollAreaSize
    const top = box.top + autoScrollAreaSize + scrollOffsetTop
    const bottom = box.top + box.height - autoScrollAreaSize
    const right = box.left + box.width - autoScrollAreaSize

    if (x < left || x > right || y < top || y > bottom) {
      if (x < left) state.dx = x - left
      else if (x > right) state.dx = x - right
      else state.dx = 0

      if (y < top) state.dy = y - top
      else if (y > bottom) state.dy = y - bottom
      else state.dy = 0

      if (!state.timer) state.timer = requestAnimationFrame(scroll)
    } else {
      stopTimer()
    }
  })

  function clampScrollIncrement(value: number) {
    // Zero means we don't want to move, so respect that.
    if (value === 0) return 0

    const min = 1
    const max = 7

    const acceleratedValue = Math.round(value * autoScrollSpeed)

    return clamp(
      acceleratedValue,
      value < 0 ? { min: -max, max: -min } : { min, max },
    )
  }

  function start() {
    window.addEventListener("dragover", move, true)
  }

  function stop() {
    window.removeEventListener("dragover", move, true)
    stopTimer()
  }

  return {
    start,
    stop,
  }
}

function getScrollerBox(scroller: Element) {
  const isPageViewport = scroller === document.scrollingElement
  const box = scroller.getBoundingClientRect()

  if (!isPageViewport) return box

  // getBoundingClientRect on the scrollingElement is special
  // and doesn't include its own scrollX/scrollY in the calculation.
  return {
    left: box.left + window.scrollX,
    top: box.top + window.scrollY,
    width: box.width,
    height: box.height,
  }
}

interface AutoScrollState {
  dx: number
  dy: number
  timer?: number
}
